/**
 * pinmerge design tokens — single source of truth for visual values.
 *
 * Consumed by:
 * - apps/widget   → template-literal interpolation in src/styles.ts
 * - apps/dashboard → codegen → app/_tokens.generated.css
 *
 * Edit values here. Never edit the generated CSS file or widget hex codes directly.
 */

/* ───── Raw palette (primitives) ─────
 * Tailwind zinc + full 50–950 scales for red / emerald / amber / blue
 * (sourced from Tailwind v3.4 / v4, identical values) + one custom zinc
 * shade + pure black/white. Use the semantic tokens below in app code
 * where possible — reach for raw palette values only for charts, the
 * /ds showcase, or one-off accents that have no semantic home. */
export const palette = {
  // Zinc — neutral surface scale (incremental, only the shades we actually use)
  zinc50:  '#fafafa',
  zinc100: '#f4f4f5',
  zinc200: '#e4e4e7',
  zinc300: '#d4d4d8',
  zinc400: '#a1a1aa',
  zinc500: '#71717a',
  zinc600: '#52525b',
  zinc700: '#3f3f46',
  zinc800: '#27272a',
  zinc850: '#1f1f23', // custom — subtle divider between 800 and 900
  zinc900: '#18181b',
  zinc950: '#09090b',

  // Red — destructive / error (no longer brand)
  red50:   '#fef2f2',
  red100:  '#fee2e2',
  red200:  '#fecaca',
  red300:  '#fca5a5',
  red400:  '#f87171',
  red500:  '#ef4444',
  red600:  '#dc2626',
  red700:  '#b91c1c',
  red800:  '#991b1b',
  red900:  '#7f1d1d',
  red950:  '#450a0a',

  // Teal — brand (anchor: teal500 = #0C485E, dark deep teal)
  teal50:   '#ecf6fa',
  teal100:  '#d4eaf2',
  teal200:  '#a8d5e4',
  teal300:  '#75bcd3',
  teal400:  '#3795b5',
  teal500:  '#0c485e',
  teal600:  '#0a3e51',
  teal700:  '#083343',
  teal800:  '#062735',
  teal900:  '#041d27',
  teal950:  '#02131a',

  // Emerald — success
  emerald50:  '#ecfdf5',
  emerald100: '#d1fae5',
  emerald200: '#a7f3d0',
  emerald300: '#6ee7b7',
  emerald400: '#34d399',
  emerald500: '#10b981',
  emerald600: '#059669',
  emerald700: '#047857',
  emerald800: '#065f46',
  emerald900: '#064e3b',
  emerald950: '#022c22',

  // Amber — pending / warning
  amber50:   '#fffbeb',
  amber100:  '#fef3c7',
  amber200:  '#fde68a',
  amber300:  '#fcd34d',
  amber400:  '#fbbf24',
  amber500:  '#f59e0b',
  amber600:  '#d97706',
  amber700:  '#b45309',
  amber800:  '#92400e',
  amber900:  '#78350f',
  amber950:  '#451a03',

  // Blue — accent / other reviewer
  blue50:   '#eff6ff',
  blue100:  '#dbeafe',
  blue200:  '#bfdbfe',
  blue300:  '#93c5fd',
  blue400:  '#60a5fa',
  blue500:  '#3b82f6',
  blue600:  '#2563eb',
  blue700:  '#1d4ed8',
  blue800:  '#1e40af',
  blue900:  '#1e3a8a',
  blue950:  '#172554',

  // ───── Zen-Mono warm neutrals (sand ramp) — primary surface scale ─────
  // Warm, low-chroma neutral replacing zinc as the app's surface/ink family.
  sand50:  '#f9f5ec',
  sand100: '#e9e4d8',
  sand200: '#d8d2c4',
  sand300: '#c7c0b1',
  sand400: '#a89f8f',
  sand500: '#817b6f',
  sand600: '#5e5a52',
  sand700: '#423f3b',
  sand800: '#2c2b29',
  sand900: '#1d1d1c',
  sand950: '#100f0f',

  // Zen-Mono named warm tints (not on the sand ramp).
  ink:          '#1e1e1e', // light-mode ink (near-black, neutral)
  cream:        '#e6e4d7', // light primary-foreground / on-dark warm highlight
  parchment:    '#e8e3da', // dark-mode foreground (warm off-white)
  silver:       '#d1cfc0', // dark-mode primary (warm light grey)
  warmGray:     '#8e8a83', // muted-foreground (dark)
  warmGrayDim:  '#66635c', // subtle text (dark)
  warmGrayDark: '#4a4743', // disabled text (dark)
  charcoal:     '#2e2e2e', // light-mode primary (neutral charcoal)
  charcoalDeep: '#363636', // dark primary-foreground
  bgDark:       '#141414', // dark page background
  cardDark:     '#1c1c1c', // dark card / popover
  secDark:      '#222222', // dark elevated surface (secondary/muted/accent)
  mutedDark:    '#2a2a2a', // dark inset
  borderDark:   '#2c2c2c', // dark border

  // Pin component colors — off-theme, identical in light & dark, tuned for
  // high visibility over arbitrary prototype backgrounds (own = warm orange,
  // other = violet). Consumed only via the `pin` semantic group below.
  pinOwn:        '#f97316',
  pinOwnHover:   '#ea580c',
  pinOther:      '#8b5cf6',
  pinOtherHover: '#7c3aed',

  white:   '#ffffff',
  black:   '#000000',
} as const;

/* ───── Semantic tokens ─────
 * What code references. `colors` is the dark-mode mapping and remains the
 * default consumed by the widget (dark-only) and existing dashboard code.
 * `colorsLight` mirrors the same shape with light-mode values; the dashboard
 * codegen emits both as `:root` (light) and `.dark` (dark) blocks so the
 * dashboard can toggle via the `dark` class on <html>. */
export const colors = {
  bg: {
    base:     palette.bgDark,   // page background
    card:     palette.cardDark, // card / popover / sidebar
    elevated: palette.secDark,  // hover surface / secondary / muted / accent
    inset:    palette.mutedDark, // kbd-style inset
  },

  text: {
    base:     palette.parchment,    // primary (warm off-white)
    bright:   palette.parchment,    // header text on dark
    muted:    palette.warmGray,     // secondary / muted-foreground
    subtle:   palette.warmGrayDim,  // tertiary / kbd
    disabled: palette.warmGrayDark, // disabled button label
  },

  brand: {
    primary:       palette.silver,       // neutral mono primary (warm light grey)
    primaryHover:  palette.parchment,
    primaryFg:     palette.charcoalDeep,
    accentLight:   palette.mutedDark,
    accentLighter: palette.secDark,
  },

  accent: {
    other:      palette.blue500, // legacy — pins now use the `pin` group
    otherHover: palette.blue600,
  },

  state: {
    error:   palette.red500,   // Zen destructive (dark)
    success: palette.emerald400,
    pending: palette.amber400,
  },

  border: {
    base:   palette.borderDark,
    strong: palette.secDark,
    subtle: palette.cardDark,
    focus:  palette.warmGray,
  },

  input: {
    border: palette.borderDark,
  },

  overlay: {
    backdrop: 'rgba(0,0,0,0.70)',
  },

  pin: {
    own:        palette.pinOwn,
    ownHover:   palette.pinOwnHover,
    other:      palette.pinOther,
    otherHover: palette.pinOtherHover,
  },
} as const;

/* Light-mode mirror of `colors`. Same semantic shape, light surface values.
 * Zen-Mono: warm sand surfaces + neutral charcoal ink/primary (no brand hue).
 * The `pin.*` group stays identical to dark — pins are off-theme overlays. */
export const colorsLight = {
  bg: {
    base:     palette.sand100,  // warm sand page background
    card:     palette.sand50,   // card / popover / sidebar (lighter than bg)
    elevated: palette.sand200,  // hover surface / secondary / muted / accent
    inset:    palette.sand300,  // kbd-style inset
  },

  text: {
    base:     palette.ink,
    bright:   palette.ink,
    muted:    palette.sand600,
    subtle:   palette.sand500,
    disabled: palette.sand400,
  },

  brand: {
    primary:       palette.charcoal,  // neutral mono primary
    primaryHover:  palette.ink,
    primaryFg:     palette.cream,
    accentLight:   palette.sand300,
    accentLighter: palette.sand200,
  },

  accent: {
    other:      palette.blue500, // legacy — pins now use the `pin` group
    otherHover: palette.blue600,
  },

  state: {
    error:   palette.red600,   // Zen destructive (light)
    success: palette.emerald500,
    pending: palette.amber500,
  },

  border: {
    base:   palette.sand300,
    strong: palette.sand400,
    subtle: palette.sand200,
    focus:  palette.sand600,
  },

  input: {
    border: palette.sand300,
  },

  overlay: {
    backdrop: 'rgba(0,0,0,0.50)',
  },

  pin: {
    own:        palette.pinOwn,
    ownHover:   palette.pinOwnHover,
    other:      palette.pinOther,
    otherHover: palette.pinOtherHover,
  },
} as const;

/* ───── Radii ─────
 * Only the base value is shared; downstream scales live in each app's
 * styling layer (Tailwind @theme inline in dashboard, ad-hoc in widget). */
export const radius = {
  base: '0.5rem',
} as const;

/* ───── Alpha helper ─────
 * `alpha(palette.teal500, 0.12)` → 'rgba(12,72,94,0.12)'.
 * Lets template literals stay readable without enumerating every tint. */
export const alpha = (hex: string, a: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${a})`;
};

export type Palette = typeof palette;
export type Colors = typeof colors;
export type ColorsLight = typeof colorsLight;
export type Radius = typeof radius;
